/**
 * Calculate a sort_order value between two existing values.
 * Uses fractional indexing to avoid reordering all items.
 *
 * @param before - The sort_order of the item before the target position (null if inserting at beginning)
 * @param after - The sort_order of the item after the target position (null if inserting at end)
 * @param defaultGap - The gap to use when inserting at the end (default 65536)
 * @returns A sort_order value between `before` and `after`
 */
export const calculateSortOrderBetween = (
  before: number | null,
  after: number | null,
  defaultGap: number = 65536
): number => {
  if (before === null && after === null) return defaultGap;
  if (before === null) return after! / 2;
  if (after === null) return before + defaultGap;
  return (before + after) / 2;
};

/**
 * Calculate the sort_order for inserting an item at a specific index in a list.
 * The list should contain items with sort_order properties.
 *
 * @param items - Array of items with sort_order property (will be sorted internally)
 * @param targetIndex - The index where the new item should be inserted
 * @returns A sort_order value appropriate for the target index
 */
export const calculateSortOrderAtIndex = <T extends { sort_order: number }>(
  items: T[],
  targetIndex: number
): number => {
  const sorted = [...items].sort((a, b) => a.sort_order - b.sort_order);
  const before = targetIndex > 0 ? sorted[targetIndex - 1].sort_order : null;
  const after = targetIndex < sorted.length ? sorted[targetIndex].sort_order : null;
  return calculateSortOrderBetween(before, after);
};
